import { Popover } from 'module/ui/components/atoms/popover';
import { MessageSection } from '@rocketlane/rocket-llm';
import { FC } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { preprocessMessageWithCitations } from 'utils/citation-preprocessor';
import {
  Label,
  CitationDataPopoverContainer,
  CitationDataWrapper,
} from 'components/organisms/document-editor/extensions/citation/ui/popovers/shared-styles';

const CitationDataContainer = styled.div<{ $hasGreyBackground?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding: 0 var(--spacing-05);
  background-color: ${({ $hasGreyBackground }) =>
    $hasGreyBackground ? 'var(--scarlet-gray-30)' : 'transparent'};
  border-radius: ${({ $hasGreyBackground }) =>
    $hasGreyBackground ? '8px' : '0'};
`;

const StyledCitationContent = styled.p`
  font-style: italic;
`;
const CitationTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  background-color: var(--gray-50);
  border-radius: 50%;
  font-size: 0.5rem;
  color: white;
  line-height: 1;
  cursor: pointer;
  margin-left: 0.2rem;
`;

const StyledPopover = styled(Popover)`
  vertical-align: middle;
`;

interface TextCitationContent {
  content: string;
  citations?: Array<{
    id: string;
    index: number;
    props?: {
      chunk_content?: string;
      meeting_name?: string;
      subject?: string;
      [key: string]: unknown;
    };
  }>;
}

export const TextCitationRendererComponent: FC<{
  section: any;
}> = ({ section }) => {
  const content = section.left_content?.value as TextCitationContent;
  const text = content?.content || '';
  const citations = content?.citations;

  // Create markdown components with citation support – use direct Popover with chunk_content
  const createMarkdownComponents = (): Partial<Components> => ({
    cite: (props: any) => {
      const citationNum = props['data-num'];
      const citation = citations?.find(
        (c) => c.index.toString() === citationNum,
      );
      const props_data = citation?.props as
        | {
            chunk_content?: string;
            meeting_name?: string;
            subject?: string;
            body?: string;
          }
        | undefined;
      const meetingName = props_data?.meeting_name;
      const subject = props_data?.subject;
      const chunkContent = props_data?.chunk_content;
      const body = props_data?.body;
      const title = meetingName ?? subject;
      const hasContent = Boolean(body || chunkContent);

      if (!hasContent) {
        return <CitationTrigger>{citationNum}</CitationTrigger>;
      }

      return (
        <StyledPopover
          content={
            <CitationDataPopoverContainer>
              {title && <Label>{title}</Label>}
              {chunkContent && (
                <CitationDataWrapper $hasGreyBackground={Boolean(title)}>
                  <CitationDataContainer>
                    <StyledCitationContent>
                      {chunkContent}
                    </StyledCitationContent>
                  </CitationDataContainer>
                </CitationDataWrapper>
              )}
            </CitationDataPopoverContainer>
          }
          trigger="hover"
        >
          <CitationTrigger>{citationNum}</CitationTrigger>
        </StyledPopover>
      );
    },
  });

  const INITIAL_COMPONENTS: Partial<Components> = {
    h1: (props) => <h1 className="text-2xl !font-bold" {...props} />,
    h2: (props) => (
      <h2 className="text-lg !font-bold mt-4 ms-0 me-0 mb-1" {...props} />
    ),
    h3: (props) => (
      <h3 className="text-base !font-bold mt-4 ms-0 me-0 mb-1" {...props} />
    ),
    h4: (props) => <h4 className="text-sm !font-bold" {...props} />,
    h5: (props) => <h5 className="text-xs !font-bold" {...props} />,
    h6: (props) => <h6 className="text-xs !font-bold" {...props} />,
    ul: (props) => (
      <ul className="list-disc list-outside pt-0 pb-0 pr-0 ps-6" {...props} />
    ),
    ol: (props) => (
      <ol
        className="list-decimal list-outside pt-0 pb-0 pr-0 ps-6"
        {...props}
      />
    ),
    li: ({ children, ...props }) => (
      <li className="text-base ps-1.5" {...props}>
        <p className="mb-0 leading-28">{children}</p>
      </li>
    ),
    p: (props) => <p className="mb-1" {...props} />,

    blockquote: (props) => (
      <blockquote className="pt-2 pb-2 pr-0 ps-6" {...props} />
    ),
    hr: (props) => (
      <hr
        className="mt-7 mb-7 border border-[var(--divider-default)]"
        {...props}
      />
    ),
    td: (props) => (
      <td
        className="px-03 py-02 text-sm align-top"
        data-col-size="sm"
        {...props}
      />
    ),
    th: (props) => (
      <th
        className="px-03 py-04 text-left text-xs font-semibold uppercase tracking-wider"
        data-col-size="sm"
        {...props}
      />
    ),
    table: (props) => (
      <div className="table-container">
        <div className="table-wrapper">
          <table
            className="border-collapse table-auto text-sm w-full min-w-[var(--thread-content-width)]"
            {...props}
          />
        </div>
      </div>
    ),
    tbody: (props) => <tbody {...props} />,
    tr: (props) => <tr {...props} />,
    thead: (props) => <thead {...props} />,
  };
  const markdownComponents = createMarkdownComponents();

  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeRaw]]}
        components={{ ...INITIAL_COMPONENTS, ...markdownComponents }}
      >
        {preprocessMessageWithCitations(text)}
      </ReactMarkdown>
    </div>
  );
};

export const TextCitationRenderer = {
  canHandle: (section: MessageSection): boolean => {
    const leftContent = section.left_content;

    if (!leftContent || !leftContent.value) {
      return false;
    }

    if ((leftContent.type as string) !== 'text_citation') {
      return false;
    }

    // Check if type is text_citation
    if ((leftContent.type as string) === 'text_citation') {
      return true;
    }

    // Check if value structure matches text_citation format
    const value = leftContent.value;

    if (
      typeof value === 'object' &&
      value !== null &&
      'content' in value &&
      typeof (value as any).content === 'string'
    ) {
      // Check if citations array exists (optional)
      return 'citations' in value || true; // Allow even without citations
    }

    return false;
  },
  Component: TextCitationRendererComponent,
};
